import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {
    type OrganizationalUnit,
    OrganizationalUnitStatus,
    organizationalUnitValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import {
    type OrganizationalUnitTemplate,
    OrganizationalUnitTemplateStatus,
    organizationalUnitTemplateValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {OrganizationalUnitTemplateAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplateAdditionalColumns";
import {plutoCacheableGet, plutoPost, type PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import {CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT} from "~/utilities/queries";
import {z as zod} from "zod";

/** Preferred template UUID for Tuffex warehouse / SAP plant setup (display-name fallback accepted). */
export const TUFFEX_WAREHOUSE_OU_TEMPLATE_ID = "a8b3c4d5-e6f7-4890-b123-456789abcdef" as Uuid;
export const TUFFEX_OU_PLANT_ID_COLUMN_ID = "b9c4d5e6-f7a8-4901-a234-567890abcdef" as Uuid;
export const TUFFEX_OU_STORAGE_LOCATION_COLUMN_ID = "f3a8b2c1-9d4e-4f7a-b823-1c2d3e4f5a6b" as Uuid;
export const TUFFEX_WAREHOUSE_OU_TEMPLATE_DISPLAY_NAME = "Tuffex Warehouse";

export const TUFFEX_DEFAULT_WAREHOUSE_PLANT_ID = "1000";
export const TUFFEX_DEFAULT_WAREHOUSE_STORAGE_LOCATION = "0001";

export type TuffexWarehouseColumnMapping = {
    plantIdColumnId: Uuid;
    storageLocationColumnId: Uuid;
    templateId: Uuid;
};

export async function fetchOrganizationalUnitTemplates(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Array<OrganizationalUnitTemplate>>> {
    return plutoCacheableGet({
        accessToken: context.accessToken,
        input: {},
        intellsysBaseUrl: context.intellsysBaseUrl,
        path: "/organizational-unit-templates/get",
        responseValidator: zod.array(organizationalUnitTemplateValidator),
        workspaceId: context.workspaceId,
    });
}

function findTuffexWarehouseTemplate(
    templates: Array<OrganizationalUnitTemplate>,
): OrganizationalUnitTemplate | undefined {
    const byId = templates.find((entry) => {
        return entry.id === TUFFEX_WAREHOUSE_OU_TEMPLATE_ID && entry.status === OrganizationalUnitTemplateStatus.active;
    });
    if (byId !== undefined) {
        return byId;
    }

    return templates.find((entry) => {
        return (
            entry.displayName === TUFFEX_WAREHOUSE_OU_TEMPLATE_DISPLAY_NAME &&
            entry.status === OrganizationalUnitTemplateStatus.active
        );
    });
}

function resolveColumnIdByDisplayName(
    template: OrganizationalUnitTemplate,
    displayName: string,
    preferredId: Uuid,
): Uuid {
    const byPreferredId = template.additionalColumns.find((column) => {
        return column.id === preferredId;
    });
    if (byPreferredId !== undefined) {
        return byPreferredId.id;
    }

    const byDisplayName = template.additionalColumns.find((column) => {
        return column.displayName === displayName;
    });
    if (byDisplayName !== undefined) {
        return byDisplayName.id;
    }

    return preferredId;
}

export function resolveTuffexWarehouseColumnMapping(
    templates: Array<OrganizationalUnitTemplate>,
): Result<TuffexWarehouseColumnMapping> {
    const template = findTuffexWarehouseTemplate(templates);
    if (template === undefined) {
        return errorResult(
            new Error(
                "Tuffex warehouse OU template not found. Run organizational unit template setup (Bruno 16 Admin Organizational Units) first.",
            ),
        );
    }

    return dataResult({
        plantIdColumnId: resolveColumnIdByDisplayName(template, "Plant ID", TUFFEX_OU_PLANT_ID_COLUMN_ID),
        storageLocationColumnId: resolveColumnIdByDisplayName(
            template,
            "Storage Location",
            TUFFEX_OU_STORAGE_LOCATION_COLUMN_ID,
        ),
        templateId: template.id,
    });
}

export async function loadTuffexWarehouseColumnMapping(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexWarehouseColumnMapping>> {
    const templatesResult = await fetchOrganizationalUnitTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }
    return resolveTuffexWarehouseColumnMapping(templatesResult.data);
}

async function createTuffexWarehouseOrganizationalUnitTemplate(
    context: PlutoWorkspaceClientContext,
    templateId: Uuid,
): Promise<Result<unknown>> {
    return plutoPost({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        workspaceId: context.workspaceId,
        path: "/organizational-unit-templates/create",
        body: {
            organizationalUnitTemplate: {
                id: templateId,
                displayName: TUFFEX_WAREHOUSE_OU_TEMPLATE_DISPLAY_NAME,
                description: "Warehouse / SAP plant + storage location for Ottokart inventory",
                instanceDisplayNameTemplate: "{{Plant ID}} / {{Storage Location}}",
                parentId: null,
                configuration: {},
                additionalColumns: [
                    {
                        id: TUFFEX_OU_PLANT_ID_COLUMN_ID,
                        displayName: "Plant ID",
                        type: OrganizationalUnitTemplateAdditionalColumnType.text,
                        configuration: {
                            required: false,
                        },
                    },
                    {
                        id: TUFFEX_OU_STORAGE_LOCATION_COLUMN_ID,
                        displayName: "Storage Location",
                        type: OrganizationalUnitTemplateAdditionalColumnType.text,
                        configuration: {
                            required: false,
                        },
                    },
                ],
            },
        },
        responseValidator: zod.unknown(),
    });
}

export async function ensureTuffexWarehouseOrganizationalUnitTemplate(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexWarehouseColumnMapping>> {
    const templatesResult = await fetchOrganizationalUnitTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const existingTemplate = findTuffexWarehouseTemplate(templatesResult.data);
    if (existingTemplate !== undefined) {
        return resolveTuffexWarehouseColumnMapping(templatesResult.data);
    }

    const preferredCreateResult = await createTuffexWarehouseOrganizationalUnitTemplate(
        context,
        TUFFEX_WAREHOUSE_OU_TEMPLATE_ID,
    );
    if (preferredCreateResult.success === true) {
        return dataResult({
            plantIdColumnId: TUFFEX_OU_PLANT_ID_COLUMN_ID,
            storageLocationColumnId: TUFFEX_OU_STORAGE_LOCATION_COLUMN_ID,
            templateId: TUFFEX_WAREHOUSE_OU_TEMPLATE_ID,
        });
    }

    // Preferred fixed id may already be owned by another workspace on shared/dev DBs.
    const fallbackTemplateId = crypto.randomUUID() as Uuid;
    const fallbackCreateResult = await createTuffexWarehouseOrganizationalUnitTemplate(
        context,
        fallbackTemplateId,
    );
    if (fallbackCreateResult.success !== true) {
        return errorResult(fallbackCreateResult.error);
    }

    return dataResult({
        plantIdColumnId: TUFFEX_OU_PLANT_ID_COLUMN_ID,
        storageLocationColumnId: TUFFEX_OU_STORAGE_LOCATION_COLUMN_ID,
        templateId: fallbackTemplateId,
    });
}

export async function listAllOrganizationalUnits(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Array<OrganizationalUnit>>> {
    return plutoCacheableGet({
        accessToken: context.accessToken,
        input: CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
        intellsysBaseUrl: context.intellsysBaseUrl,
        path: "/organizational-units/get-all",
        responseValidator: zod.array(organizationalUnitValidator),
        workspaceId: context.workspaceId,
    });
}

function normalizeOptionalWarehouseField(value: string | null | undefined): string {
    if (value === null || value === undefined) {
        return "";
    }
    return value.trim();
}

function organizationalUnitMatchesPlantAndStorage({
    columnMapping,
    organizationalUnit,
    plantId,
    storageLocation,
}: {
    columnMapping: TuffexWarehouseColumnMapping;
    organizationalUnit: OrganizationalUnit;
    plantId: string;
    storageLocation: string;
}): boolean {
    const ouPlantId = organizationalUnit.additionalColumnValues[columnMapping.plantIdColumnId];
    const ouStorageLocation = organizationalUnit.additionalColumnValues[columnMapping.storageLocationColumnId];
    const normalizedOuPlantId = typeof ouPlantId === "string" ? ouPlantId.trim() : "";
    const normalizedOuStorageLocation = typeof ouStorageLocation === "string" ? ouStorageLocation.trim() : "";
    return normalizedOuPlantId === plantId && normalizedOuStorageLocation === storageLocation;
}

function coerceOptionalWarehouseField(value: unknown): string | null {
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return String(value);
    }
    return null;
}

export function getOrganizationalUnitPlantAndStorage({
    columnMapping,
    organizationalUnit,
}: {
    columnMapping: TuffexWarehouseColumnMapping;
    organizationalUnit: OrganizationalUnit;
}): {
    plantId: string | null;
    storageLocation: string | null;
} {
    return {
        plantId: coerceOptionalWarehouseField(
            organizationalUnit.additionalColumnValues[columnMapping.plantIdColumnId],
        ),
        storageLocation: coerceOptionalWarehouseField(
            organizationalUnit.additionalColumnValues[columnMapping.storageLocationColumnId],
        ),
    };
}

export async function createWarehouseOrganizationalUnit({
    accessToken,
    columnMapping,
    parentId = null,
    plantId,
    intellsysBaseUrl,
    storageLocation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    columnMapping: TuffexWarehouseColumnMapping;
    parentId?: Uuid | null;
    plantId: string;
    storageLocation: string;
}): Promise<Result<void>> {
    const additionalColumnValues: Record<string, unknown> = {
        [columnMapping.plantIdColumnId]: plantId,
        [columnMapping.storageLocationColumnId]: storageLocation,
    };

    const createResult = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/organizational-units/create",
        body: {
            organizationalUnit: {
                additionalColumnValues: additionalColumnValues,
                parentId: parentId,
                templateId: columnMapping.templateId,
            },
        },
        responseValidator: zod.unknown(),
    });
    if (createResult.success !== true) {
        return errorResult(createResult.error);
    }

    return dataResult(undefined);
}

async function findOrganizationalUnitIdByPlantAndStorage({
    columnMapping,
    context,
    plantId,
    storageLocation,
}: {
    columnMapping: TuffexWarehouseColumnMapping;
    context: PlutoWorkspaceClientContext;
    plantId: string;
    storageLocation: string;
}): Promise<Result<Uuid | null>> {
    const organizationalUnitsResult = await listAllOrganizationalUnits(context);
    if (organizationalUnitsResult.success !== true) {
        return organizationalUnitsResult;
    }

    const match = organizationalUnitsResult.data.find((organizationalUnit) => {
        if (organizationalUnit.status !== OrganizationalUnitStatus.active) {
            return false;
        }
        if (organizationalUnit.templateId !== columnMapping.templateId) {
            return false;
        }
        return organizationalUnitMatchesPlantAndStorage({
            columnMapping: columnMapping,
            organizationalUnit: organizationalUnit,
            plantId: plantId,
            storageLocation: storageLocation,
        });
    });

    if (match === undefined) {
        return dataResult(null);
    }

    return dataResult(match.id);
}

export async function resolveOrganizationalUnitIdForWarehouse({
    plantId,
    storageLocation,
    ...context
}: PlutoWorkspaceClientContext & {
    plantId?: string | null;
    storageLocation?: string | null;
}): Promise<Result<Uuid>> {
    const columnMappingResult = await ensureTuffexWarehouseOrganizationalUnitTemplate(context);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const normalizedPlantId = normalizeOptionalWarehouseField(plantId);
    const normalizedStorageLocation = normalizeOptionalWarehouseField(storageLocation);
    const effectivePlantId = normalizedPlantId.length > 0 ? normalizedPlantId : TUFFEX_DEFAULT_WAREHOUSE_PLANT_ID;
    const effectiveStorageLocation = normalizedStorageLocation.length > 0
        ? normalizedStorageLocation
        : TUFFEX_DEFAULT_WAREHOUSE_STORAGE_LOCATION;

    const existingOuIdResult = await findOrganizationalUnitIdByPlantAndStorage({
        columnMapping: columnMappingResult.data,
        context: context,
        plantId: effectivePlantId,
        storageLocation: effectiveStorageLocation,
    });
    if (existingOuIdResult.success !== true) {
        return existingOuIdResult;
    }

    if (existingOuIdResult.data !== null) {
        return dataResult(existingOuIdResult.data);
    }

    const createResult = await createWarehouseOrganizationalUnit({
        ...context,
        columnMapping: columnMappingResult.data,
        plantId: effectivePlantId,
        storageLocation: effectiveStorageLocation,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    const createdOuIdResult = await findOrganizationalUnitIdByPlantAndStorage({
        columnMapping: columnMappingResult.data,
        context: context,
        plantId: effectivePlantId,
        storageLocation: effectiveStorageLocation,
    });
    if (createdOuIdResult.success !== true) {
        return createdOuIdResult;
    }
    if (createdOuIdResult.data === null) {
        return errorResult(new Error("Warehouse organizational unit was created but could not be loaded."));
    }

    return dataResult(createdOuIdResult.data);
}

export async function resolveDefaultOrganizationalUnitId(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Uuid>> {
    return resolveOrganizationalUnitIdForWarehouse({
        ...context,
        plantId: TUFFEX_DEFAULT_WAREHOUSE_PLANT_ID,
        storageLocation: TUFFEX_DEFAULT_WAREHOUSE_STORAGE_LOCATION,
    });
}

export async function buildOrganizationalUnitByIdMap(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Map<Uuid, OrganizationalUnit>>> {
    const organizationalUnitsResult = await listAllOrganizationalUnits(context);
    if (organizationalUnitsResult.success !== true) {
        return organizationalUnitsResult;
    }

    const organizationalUnitById = new Map<Uuid, OrganizationalUnit>();
    for (const organizationalUnit of organizationalUnitsResult.data) {
        organizationalUnitById.set(organizationalUnit.id, organizationalUnit);
    }

    return dataResult(organizationalUnitById);
}
